import type { LandingPage } from './types'

/**
 * Longer SEO copy ("about" section below the offers) for the region×dimension
 * combos, keyed `<regionSlug>/<baseSlug>` like `comboPerexes.ts`. Every indexed
 * combo (combos.generated.ts) should have one. Keep it unique per combo, tied to
 * the region's real places and seasons, and never promise facilities the page
 * filter doesn't imply.
 */
export const comboAbouts: Record<string, NonNullable<LandingPage['about']>> = {
  'krkonose/chalupy': {
    heading: 'Chalupa v Krkonoších',
    paragraphs: [
      'Krkonošské chalupy mají svůj typický vzhled: roubené nebo hrázděné stavby na loukách nad údolími, často staré budy, kde se dřív chovaly krávy a vařil sýr. Dnes jsou z nich útulné chalupy s vlastní kuchyní a výhledem na hřebeny.',
      'Z chalupy se dá vyrazit pěšky na Sněžku, do Obřího dolu nebo k pramenům Labe, v zimě na sjezdovky ve Špindlerově Mlýně, Peci nebo Rokytnici. Na rozdíl od hotelu si den uspořádáte po svém a večer nemusíte nikam chodit.',
      'Chalupy v Krkonoších se na hlavní sezonu zamlouvají měsíce dopředu. Last-minute termíny jsou nejjednodušší cesta, jak se sem dostat i bez dlouhého plánování.',
    ],
  },
  'krkonose/glamping': {
    heading: 'Glamping v Krkonoších',
    paragraphs: [
      'Glamping v Krkonoších je pro ty, kdo chtějí mít hory co nejblíž: ráno vylezete ze stanu nebo domku a máte před sebou louky, les a hřebeny. Místo spacáku čeká postel a místo kempu klid.',
      'Nejlepší čas je od pozdního jara do podzimu, kdy se dá vyrazit na túru k Mumlavskému vodopádu, na Zlaté návrší nebo kolem Labské boudy. Na horách se ale počasí mění rychle a noci bývají chladné i v létě – v popisu si ověřte, jak se v glampingu topí.',
    ],
  },
  'krkonose/pobyty-na-horach': {
    heading: 'Pobyt v Krkonoších po celý rok',
    paragraphs: [
      'Krkonoše jsou nejvyšší české hory a každá sezona tu má jinou podobu. Zima patří lyžování ve Špindlerově Mlýně, Peci pod Sněžkou, Harrachově či Rokytnici nad Jizerou a běžkám po hřebenech, léto turistice od Luční boudy po Sněžné jámy.',
      'Na jaře taje sníh a vodopády jsou nejmohutnější, na podzim se hory vyprázdní a výhledy bývají nejdelší. Pro méně zdatné je tu lanovka na Sněžku z Pece nebo na Černou horu z Janských Lázní.',
      'Mezi horskými pobyty najdete chalupy, roubenky i apartmány přímo ve střediscích nebo na odlehlejších místech. Vyberte termín, který vám sedí, a vyrazte.',
    ],
  },
  'krkonose/pro-psy': {
    heading: 'Se psem do Krkonoš',
    paragraphs: [
      'Krkonoše jsou pro psy skvělé – dlouhé hřebenovky, lesní cesty a potoky, kde se dá napít. Pamatujte, že v Krkonošském národním parku patří pes na vodítko, a to kvůli zvěři i ostatním turistům.',
      'S pejskem se dobře chodí třeba po Cestě česko-polského přátelství, k Labskému vodopádu nebo kolem Malé Úpy. Lanovky psy obvykle převážejí, ale podmínky se liší, proto si je ověřte předem.',
      'Než rezervujete, podívejte se v popisu ubytování, kolik psů je povoleno a zda se za ně platí. Filtr na mazlíčky je tu zapnutý automaticky.',
    ],
  },
  'krkonose/rodinne-pobyty': {
    heading: 'Krkonoše s dětmi',
    paragraphs: [
      'S dětmi se v Krkonoších nudit nebudete. Stezka korunami stromů v Janských Lázních vede až do výšky nad lesem, lanovka na Sněžku zkrátí cestu na vrchol a podél Labe nebo Mumlavy vedou nenáročné trasy k vodopádům.',
      'Krkonoše jsou také krajem Krakonoše, pána hor z pohádek a pověstí, takže se dá z každého výletu udělat malé dobrodružství. V zimě jsou ve střediscích dětské lyžařské školy a mírné svahy pro začátečníky.',
      'U rodinných pobytů vybíráme ubytování s vybavením pro děti a dostatkem místa. V popisu si zkontrolujte počet ložnic, postýlku a vzdálenost od sjezdovky nebo parkoviště.',
    ],
  },
  'krkonose/romanticke-pobyty': {
    heading: 'Romantický víkend v Krkonoších',
    paragraphs: [
      'Krkonoše umí být romantické – ranní mlha nad Obřím dolem, západ slunce z hřebene u Luční boudy nebo zasněžená krajina, kterou projdete na sněžnicích jen sami dva.',
      'Pro pobyt ve dvou se hodí hlavně menší chalupy a apartmány mimo nejrušnější střediska, třeba v okolí Malé Úpy, Vítkovic nebo Pasek nad Jizerou, kde je i v sezoně klid. Večer pak stačí víno, dobré jídlo a nikam nespěchat.',
    ],
  },
  'krkonose/s-krbem': {
    heading: 'Krkonošská chalupa s krbem',
    paragraphs: [
      'Krkonošská zima je dlouhá a sníh tu leží často do dubna. Po dni na sjezdovce nebo na běžkách přes Zlaté návrší nic nepřekoná oheň v krbu, suché boty a teplou večeři.',
      'Krb nebo krbová kamna mají hlavně roubenky a starší horské chalupy, kde bývají srdcem obývacího pokoje. V popisu si ověřte, jestli je dřevo v ceně a zda je krb jen pro radost, nebo i zdroj tepla.',
      'Ubytování s krbem v Krkonoších je nejžádanější od listopadu do března. Termíny se ale uvolňují i v sezoně, a právě ty tu najdete.',
    ],
  },
  'krkonose/se-saunou': {
    heading: 'Sauna po krkonošské túře',
    paragraphs: [
      'Sauna se v Krkonoších vyplatí v každé sezoně. V zimě uvolní nohy po lyžování nebo běžkách, v létě po výstupu na Sněžku či Kozí hřbety. A když je venku sníh, není nic lepšího než se po sauně vyválet v závěji.',
      'Na rozdíl od hotelového wellness máte saunu na chalupě jen pro sebe a své lidi. Mnoho chalup má saunu přímo v domě, některé venkovní sudovou na zahradě s výhledem na hřebeny.',
      'V popisu si zkontrolujte, zda je sauna v ceně pobytu, kolik lidí se do ní vejde a jestli je finská, nebo infrasauna.',
    ],
  },
  'jizni-cechy/glamping': {
    heading: 'Glamping v jižních Čechách',
    paragraphs: [
      'Jihočeská krajina je pro glamping ideální: rybníky, hráze s alejemi, lesy a široké nebe bez kopců, které by bránily ve výhledu na hvězdy. Spíte v přírodě, ale v posteli, a ráno se jdete vykoupat.',
      'Z glampingu se dá vyrazit na kole po Třeboňsku, na vodu po Lužnici nebo Vltavě, nebo na výlet do Českého Krumlova. Mnoho glampingů je na samotách, takže se vyplatí vzít si jídlo s sebou nebo si předem najít hospodu v okolí.',
      'Glampingy mají často jen jednu nebo dvě jednotky, proto se rychle obsazují. Sledujte last-minute termíny a vyrazte, když vyjde počasí.',
    ],
  },
  'jizni-cechy/pobyty-na-horach': {
    heading: 'Hory na jihu Čech',
    paragraphs: [
      'Jižní Čechy nejsou jen rybníky. Na jihu kraje se zvedají Novohradské hory s Terčiným údolím a pralesovitými lesy, u Českého Krumlova Blanský les s Kletí, kde stojí nejstarší kamenná rozhledna v Čechách, a na západě šumavské podhůří a Lipensko.',
      'V zimě se lyžuje na Lipně a na Kleti a v Novohradských horách se běžkuje. V létě jsou hory klidnější než Krkonoše, s dlouhými lesními cestami a prázdnými vrcholy, odkud za dobrého počasí uvidíte i Alpy.',
      'Horské ubytování na jihu Čech je často na samotách a v malých obcích. Vybírejte z termínů, které jsou právě volné.',
    ],
  },
  'jizni-cechy/pobyty-u-vody': {
    heading: 'Pobyt u vody v jižních Čechách',
    paragraphs: [
      'Jižní Čechy jsou kraj vody. Na Třeboňsku je několik stovek rybníků včetně Rožmberka, největšího rybníka v Česku, Lipno je největší vodní nádrž v zemi a po Vltavě z Vyššího Brodu nebo Českého Krumlova se splouvá každé léto.',
      'Na vodu se dá jezdit i mimo léto: na Lužnici na kanoi, kolem rybníků na kole po hrázích nebo na podzim na výlovy, kdy se Rožmberk a další rybníky loví za velké podívané. Orlík a Hluboká zase spojí vodu se zámkem.',
      'V nabídce najdete chaty a chalupy u rybníků, přehrad i řek. V popisu si ověřte, jak daleko je voda a jestli má ubytování vlastní přístup.',
    ],
  },
  'jizni-cechy/pro-psy': {
    heading: 'Jižní Čechy se psem',
    paragraphs: [
      'Rovinatá krajina jižních Čech je pro psy pohodlná: hráze rybníků, lesní cesty a polní stezky se dají projít celý den bez velkého převýšení. Psi s vámi zvládnou i cyklovýlet po Třeboňsku, pokud jsou na delší trasy zvyklí.',
      'Pozor na koupání: v rybnících se chovají ryby a na hrázích se ne vždy smí do vody. Pro psí koupání jsou lepší řeky, písníky nebo místa k tomu určená.',
      'Před rezervací si v popisu zkontrolujte počet povolených psů a případný poplatek. Filtr na mazlíčky je zapnutý automaticky.',
    ],
  },
  'jizni-cechy/rodinne-pobyty': {
    heading: 'Jižní Čechy s dětmi',
    paragraphs: [
      'Jižní Čechy jsou pro rodiny s dětmi jako stvořené. Krajina je rovinatá, cyklostezky po hrázích Třeboňska zvládnou i menší děti a na každém kroku je rybník, kde se dá v létě vykoupat.',
      'Na výlety se hodí Stezka korunami stromů na Lipně, zámek Hluboká, Český Krumlov s medvědy v zámeckém příkopu nebo jihočeská zoo v Hluboké nad Vltavou. Na podzim stojí za to vzít děti na výlov rybníka.',
      'Mezi rodinnými pobyty najdete chalupy a statky s velkou zahradou a dostatkem místa. Ukazujeme jen termíny, které jsou právě teď volné.',
    ],
  },
  'jizni-cechy/romanticke-pobyty': {
    heading: 'Romantika na jihu Čech',
    paragraphs: [
      'Jižní Čechy mají pro páry hodně co nabídnout. Večer v uličkách Českého Krumlova, ráno mlha nad třeboňskými rybníky, procházka parkem kolem zámku Hluboká nebo výlet lodí po Lipně.',
      'Pro romantický pobyt se hodí menší chalupy na samotách, glampingy u vody nebo zrekonstruované statky v malých vesnicích. Jihočeský venkov je klidný, s malým provozem, a dobře se tu jezdí na kole i chodí pěšky.',
      'Romantické pobyty vybíráme s ohledem na soukromí a atmosféru. Termíny se mění každý den, tak se vyplatí kouknout i ve čtvrtek před víkendem.',
    ],
  },
  'jizni-cechy/s-kadi': {
    heading: 'Venkovní káď v jižních Čechách',
    paragraphs: [
      'Dřevěná káď, roztápěná ohněm, k jihočeské krajině sedí. Sedíte v horké vodě pod širým nebem, kolem ticho, a nad vámi tmavá obloha, kterou neruší světla velkých měst.',
      'Nejsilnější zážitek je káď na podzim a v zimě, kdy se z vody kouří a venku mrzne. Káď bývá potřeba roztopit několik hodin předem, a proto se vyplatí domluvit se s majitelem na čase.',
      'V nabídce najdete chalupy a glampingy s venkovní kádí nebo vířivkou po celých jižních Čechách. V popisu si ověřte, zda je káď v ceně, nebo se platí zvlášť.',
    ],
  },
  'jizni-cechy/s-krbem': {
    heading: 'Jihočeská chalupa s krbem',
    paragraphs: [
      'Na podzim se jižní Čechy halí do mlh, rybníky se loví a lesy voní houbami. Po celodenním výletu je nejlepší sednout si ke krbu, otevřít víno a poslouchat praskání dřeva.',
      'Krb mají hlavně zrekonstruované statky, starší chalupy a roubenky na Šumavském podhůří a v Novohradských horách. V popisu si ověřte, jestli je dřevo v ceně a zda je krb hlavním zdrojem tepla.',
      'Ubytování s krbem je nejžádanější mezi říjnem a březnem. Sledujte nabídku – volné termíny přibývají průběžně.',
    ],
  },
  'jizni-cechy/s-virivkou': {
    heading: 'Vířivka na jihočeské chalupě',
    paragraphs: [
      'Vířivka je ideální tečka za jihočeským dnem: ráno na kole po hrázích, odpoledne výlet do Třeboně nebo Českého Krumlova a večer teplá voda pod hvězdami.',
      'Na chalupách bývá vířivka venku na terase nebo vnitřní v koupelně, kterou využijete i v dešti. U venkovních vířivek si ověřte, jestli fungují celoročně a kolik lidí se do nich vejde.',
      'Ubytování s vířivkou na jihu Čech je oblíbené hlavně u párů a malých skupin. Termíny se tu uvolňují průběžně a ukazujeme jen ty volné.',
    ],
  },
  'jizni-cechy/se-saunou': {
    heading: 'Sauna v jižních Čechách',
    paragraphs: [
      'Jižní Čechy jsou kraj pomalé dovolené a sauna k ní patří. Po dni na kole kolem Třeboně nebo na vodě na Lužnici se prohřejete a pak se ochladíte venku, v kádi nebo v sezoně v blízkém rybníce.',
      'Sauny najdete na chalupách, statcích i v menších penzionech od Třeboňska přes Českobudějovicko po Lipno. Některé jsou venkovní, sudové nebo v samostatném domku na zahradě.',
      'V popisu zkontrolujte, jestli je sauna v ceně a jestli je jen pro vás. Ukazujeme jen termíny, které jsou právě teď volné.',
    ],
  },
  'jizni-cechy/wellness-pobyty': {
    heading: 'Wellness pobyt na jihu Čech',
    paragraphs: [
      'Jižní Čechy mají k odpočinku blízko. Třeboň je známá slatinnými lázněmi, krajina je klidná a rovinatá a nikdo vás tu nebude honit. Ideální kraj na pár dní, kdy chcete hlavně zpomalit.',
      'Mezi wellness pobyty najdete chalupy a menší ubytování se saunou, vířivkou nebo kádí, které nemusíte s nikým sdílet. Přes den se můžete projít kolem rybníků nebo zajet do Českého Krumlova, večer odpočívat.',
      'Wellness pobyty jsou nejžádanější od podzimu do jara. Last-minute termíny se ale objevují průběžně.',
    ],
  },
  'beskydy-a-valassko/chalupy': {
    heading: 'Chalupa v Beskydech a na Valašsku',
    paragraphs: [
      'Valašské chalupy stojí často osaměle na pasekách – horských loukách, které kdysi vyklučili pastevci. Dřevěnice s pavlačí, ovocný sad a les za plotem: přesně takový obraz Valašska znáte z obrázků.',
      'Z chalupy můžete vyrazit na Radhošť, Pustevny nebo Soláň, s dětmi do Valašského muzea v přírodě v Rožnově pod Radhoštěm a v létě k vodě na přehradu v Horní Bečvě. V zimě se lyžuje ve Velkých Karlovicích nebo na Bílé.',
      'Mezi chalupami najdete místa pro dva i velké party. Ukazujeme jen termíny, které jsou právě teď volné.',
    ],
  },
  'beskydy-a-valassko/pobyty-na-horach': {
    heading: 'Na hory do Beskyd',
    paragraphs: [
      'Moravskoslezské Beskydy jsou hory hřebenovek. Na Lysou horu (1 323 m), nejvyšší vrchol pohoří, vede několik značených cest, z Pusteven se chodí po Jurkovičově stezce na Radhošť a z Bílé a Kohútky jsou výhledy až na Slovensko.',
      'V zimě se lyžuje ve Velkých Karlovicích, na Pustevnách, v Ostravici nebo na Bílé a běžkuje po hřebenech. Léto patří turistice a kolu, podzim barevným bukovým lesům.',
      'Beskydy jsou dobře dostupné z Ostravy, Olomouce i Zlína, takže se sem dá vyrazit i jen na víkend. Vybírejte z horských pobytů, které majitelé právě uvolnili.',
    ],
  },
  'beskydy-a-valassko/pro-psy': {
    heading: 'Se psem do Beskyd',
    paragraphs: [
      'Beskydy jsou pro psy ideální: dlouhé lesní cesty, horské louky a potoky na každém kroku. Pes s vámi vyjde na Lysou horu i Radhošť a na hřebenech nepotká tolik lidí jako v Krkonoších.',
      'Části Beskyd jsou chráněné jako CHKO a některé rezervace, třeba okolí Kněhyně, je dobré procházet se psem na vodítku. Pozor i na ovce na pastvinách – na Valašsku se pořád pase.',
      'V popisu ubytování najdete, kolik psů je povoleno a jestli se za ně platí. Filtr na mazlíčky je zapnutý automaticky.',
    ],
  },
  'beskydy-a-valassko/romanticke-pobyty': {
    heading: 'Romantický pobyt v Beskydech',
    paragraphs: [
      'Beskydy mají pro dva to nejlepší: samotu. Roubenka na pasece, kolem jen louky a les, ráno mlha v údolí a večer ticho, které ve městě neznáte.',
      'Přes den se můžete vydat na Pustevny a k soše Radegasta, projít se po hřebeni na Radhošť nebo zajet do Štramberku na Štramberské uši. Večer už stačí jen víno a dobré jídlo – na Valašsku si určitě dejte frgál nebo kyselici.',
    ],
  },
  'beskydy-a-valassko/s-krbem': {
    heading: 'Valašská roubenka s krbem',
    paragraphs: [
      'Roubenka a oheň patří k Valašsku stejně jako slivovice. V mnoha dřevěnicích najdete krb, krbová kamna nebo kachlová kamna, která drží teplo do rána.',
      'Po dni na běžkách na Pustevnách nebo na sjezdovce ve Velkých Karlovicích se u krbu nejlépe odpočívá. Na podzim zase po túře barevnými bukovými lesy na Smrk nebo Kněhyni.',
      'V popisu ubytování si ověřte, jestli je dřevo v ceně a zda budete topit sami. Ukazujeme jen termíny, které jsou právě teď volné.',
    ],
  },
  'beskydy-a-valassko/s-virivkou': {
    heading: 'Vířivka po beskydské túře',
    paragraphs: [
      'Beskydské túry umí dát zabrat – výstup na Lysou horu má přes 800 výškových metrů. Vířivka na chalupě je pak nejlepší odměna, ať už venku s výhledem na kopce, nebo uvnitř.',
      'Ubytování s vířivkou najdete v Beskydech hlavně v moderních chalupách a apartmánech kolem Frenštátu, Rožnova nebo Velkých Karlovic. Venkovní vířivka v zimě, kdy kolem leží sníh, je zážitek, na který se nezapomíná.',
      'V popisu si ověřte, zda je vířivka v ceně a jestli je k dispozici celoročně.',
    ],
  },
  'beskydy-a-valassko/se-saunou': {
    heading: 'Sauna v Beskydech',
    paragraphs: [
      'Na Valašsku se saunuje rádo a dlouho. Mnoho chalup a roubenek tu má vlastní saunu – v domě, v dřevěném domku na zahradě nebo sudovou s výhledem na hřebeny.',
      'Sauna se hodí po lyžování ve Velkých Karlovicích nebo na Bílé, po běžkách z Pusteven i po letní hřebenovce z Lysé hory na Smrk. Ochladit se dá v kádi, pod sprchou nebo v zimě ve sněhu.',
      'V popisu ubytování zkontrolujte, jestli je sauna v ceně a kolik lidí se do ní vejde. Termíny se tu mění každý den.',
    ],
  },
  'beskydy-a-valassko/wellness-pobyty': {
    heading: 'Wellness v Beskydech a na Valašsku',
    paragraphs: [
      'Wellness v Beskydech nemusí znamenat velký hotel. Stačí chalupa se saunou, vířivkou nebo kádí, kde po celém dni na horách odpočíváte jen vy a vaši blízcí.',
      'Kraj má k odpočinku blízko i jinak: horský vzduch, dlouhé procházky lesem a valašská kuchyně. Na lázeňskou kolonádu se dá zajet do Luhačovic, největších moravských lázní.',
      'Wellness pobyty v Beskydech jsou nejžádanější v zimě. Volné termíny se ale objevují i tehdy – sledujte nabídku.',
    ],
  },
  'jizerske-hory/chaty': {
    heading: 'Chata v Jizerských horách',
    paragraphs: [
      'Jizerské hory mají dlouhou chatařskou tradici. Chaty stojí v lesích kolem Bedřichova, Janova nad Nisou, Josefova Dolu nebo Albrechtic a jsou ideální základnou pro výlety do hor.',
      'Z chaty se dá vyrazit na běžky po Jizerské magistrále, na kole k přehradám Souš a Josefův Důl nebo pěšky na rozhledny Štěpánka a Královka. V létě se dá vykoupat v jablonecké přehradě Mšeno.',
      'Chaty v Jizerkách bývají jednodušší než velké chalupy, ale o to víc tu cítíte les. V popisu najdete vybavení, kapacitu a příjezd autem.',
    ],
  },
  'jizerske-hory/glamping': {
    heading: 'Glamping v Jizerských horách',
    paragraphs: [
      'Jizerky jsou hory rozlehlých lesů, rašelinišť a tichých údolí – ideální místo pro glamping. Spíte v přírodě, ale v pohodlí, a ráno vás budí ptáci.',
      'Z glampingu se dá vyrazit pěšky nebo na kole k rašeliništím na Jizerce, k vodopádům Černé Desné nebo k jizerskohorským bučinám, které jsou součástí světového dědictví UNESCO. Noci tu bývají chladné i v létě, proto se vyplatí ověřit, jak se v glampingu topí.',
    ],
  },
  'jizerske-hory/pobyty-na-horach': {
    heading: 'Jizerské hory v létě i v zimě',
    paragraphs: [
      'Jizerské hory jsou nižší než Krkonoše, ale sněhu tu bývá dost. V zimě patří běžkařům: Jizerská magistrála má desítky kilometrů upravených stop a každý rok se tu jede Jizerská 50. Sjezdovky najdete v Bedřichově, na Tanvaldském Špičáku nebo v Albrechticích.',
      'V létě se hory promění v síť cyklotras a turistických cest po lesních silničkách s mírným převýšením. Rozhledny jako Bramberk, Černá Studnice nebo Slovanka nabízejí výhledy na Ještěd i Krkonoše.',
      'Horské pobyty v Jizerkách jsou oblíbené hlavně kvůli blízkosti Prahy a Liberce. Ukazujeme jen termíny, které jsou právě teď volné.',
    ],
  },
  'jizerske-hory/rodinne-pobyty': {
    heading: 'Jizerské hory s dětmi',
    paragraphs: [
      'Jizerky jsou pro rodiny s dětmi skvělé – cesty jsou široké, převýšení mírné a cíle na dosah. Kočárek nebo dětské kolo zvládnou asfaltové lesní silničky kolem přehrad.',
      'Na výlety se hodí rozhledna Štěpánka, osada Jizerka, sklářské muzeum v Kristiánově nebo vodopády Černého potoka. V zimě jsou v Bedřichově a Albrechticích mírné svahy pro začínající lyžaře.',
      'Mezi rodinnými pobyty najdete chalupy a chaty s dostatkem místa a vybavením pro děti. V popisu si ověřte, kolik je ložnic a jestli je pozemek oplocený.',
    ],
  },
  'jizerske-hory/romanticke-pobyty': {
    heading: 'Romantický pobyt v Jizerkách',
    paragraphs: [
      'Jizerské hory jsou tiché, zalesněné a v zimě často zasněžené – ideální kulisa pro pobyt ve dvou. Ráno procházka kolem přehrady, odpoledne výlet do osady Jizerka a večer jen vy dva.',
      'Romantická místa v Jizerkách jsou často roubenky a chalupy na okraji obcí nebo na samotách, kde vás nikdo neruší. Pro větší soukromí hledejte ubytování s jedinou jednotkou.',
      'Last-minute termíny se hodí i na překvapení – Jizerky jsou kousek od Prahy, takže se dá vyrazit v pátek odpoledne.',
    ],
  },
  'jizerske-hory/s-krbem': {
    heading: 'Chalupa s krbem v Jizerských horách',
    paragraphs: [
      'Jizerské hory patří k nejchladnějším místům v Česku – osada Jizerka bývá v zimních mrazech na samém dně tabulek. O to víc tu oceníte krb nebo krbová kamna po dni na běžkách.',
      'Chalupy a roubenky s krbem najdete kolem Bedřichova, Josefova Dolu, Kořenova i na severní straně hor. Mnohé jsou staré horské domy, kde je oheň v krbu tradicí.',
      'Ověřte si v popisu, jestli je dřevo v ceně. Termíny se tu objevují průběžně, i v hlavní zimní sezoně.',
    ],
  },
  'jizerske-hory/s-virivkou': {
    heading: 'Ubytování s vířivkou v Jizerkách',
    paragraphs: [
      'Jizerská magistrála, sjezd na kole od Smědavy nebo celý den na rozhlednách – po takovém dni se vířivka hodí. Teplá voda uvolní nohy a večer na terase pod hvězdami je odměnou.',
      'Vířivky jsou v Jizerkách hlavně v modernějších chalupách a apartmánech. Pokud chcete venkovní vířivku v zimě, ověřte si, jestli je provozovaná celoročně.',
      'Ukazujeme jen termíny, které jsou právě teď volné, takže se dá vyrazit i na poslední chvíli.',
    ],
  },
  'jizerske-hory/se-saunou': {
    heading: 'Sauna v Jizerských horách',
    paragraphs: [
      'Kdo jezdí do Jizerek na běžky, ví, že po padesáti kilometrech na magistrále je sauna nutnost. Na chalupě ji máte jen pro sebe a můžete se v ní prohřát, kdy chcete.',
      'Sauny najdete v roubenkách, horských chalupách i v moderních apartmánech. Některé jsou sudové na zahradě, jiné přímo v domě. Ochladit se dá ve sněhu, který tu leží často až do dubna.',
      'V popisu si ověřte, zda je sauna v ceně a jaký je její typ. Termíny se uvolňují průběžně.',
    ],
  },
  'jizerske-hory/wellness-pobyty': {
    heading: 'Wellness v Jizerských horách',
    paragraphs: [
      'Jizerské hory jsou místo, kde se dá opravdu vypnout. Rozsáhlé lesy, rašeliniště a bučiny zapsané v UNESCO dělají z obyčejné procházky odpočinek, který doplní sauna nebo vířivka na chalupě.',
      'Wellness pobyty vybíráme z ubytování se soukromou saunou, vířivkou nebo kádí. Přes den se můžete projít k přehradě nebo na rozhlednu, večer už odpočívat.',
      'Jizerky jsou kousek od Prahy i Liberce – na wellness víkend se dá vyrazit i na poslední chvíli.',
    ],
  },
  'ceske-svycarsko/pro-psy': {
    heading: 'České Švýcarsko se psem',
    paragraphs: [
      'Skalní stezky Českého Švýcarska jsou pro psy dobrodružstvím – kolem pískovcových věží, lesem a podél potoků. Pejsek s vámi zvládne cestu k Pravčické bráně, okruh kolem Jetřichovických vyhlídek nebo Kyjovské údolí.',
      'V národním parku musí být pes na vodítku. Na některých vyhlídkách vedou strmé žebříky a schody, takže s velkým psem se vyplatí předem vybrat trasu.',
      'V popisu ubytování najdete pravidla pro psy. Filtr na mazlíčky je zapnutý automaticky.',
    ],
  },
  'ceske-svycarsko/romanticke-pobyty': {
    heading: 'Romantické České Švýcarsko',
    paragraphs: [
      'České Švýcarsko je jako stvořené pro dva: vyhlídky nad mořem lesů, plavba loďkou soutěskou Kamenice a západ slunce z Mariiny vyhlídky nebo Růžovského vrchu.',
      'Ubytování je tu spíš komorní – menší chalupy, chaty a penziony v obcích na okraji národního parku, kde je večer klid. Mimo letní sezonu tu bývá nečekaně málo lidí.',
      'Romantické pobyty v Českém Švýcarsku vybíráme s ohledem na soukromí a atmosféru. Termíny se objevují průběžně.',
    ],
  },
  'ceske-svycarsko/s-krbem': {
    heading: 'Chalupa s krbem v Českém Švýcarsku',
    paragraphs: [
      'Podzim a zima v Českém Švýcarsku mají zvláštní atmosféru: mlhy v údolí Labe, prázdné stezky a zasněžené skalní věže. Po dni venku je u krbu nejlíp.',
      'Chalupy s krbem najdete v obcích jako Jetřichovice, Mezná, Kyjov nebo Doubice. Některé jsou podstávkové nebo roubené domy, kde oheň v krbu patří k domu odjakživa.',
      'V popisu si ověřte, jestli je dřevo v ceně. Mimo sezonu tu najdete nejvíc volných termínů.',
    ],
  },
  'jeseniky/chaty': {
    heading: 'Chata v Jeseníkách',
    paragraphs: [
      'Chata v Jeseníkách je klasický výchozí bod na hory. Z Karlovy Studánky, Malé Morávky nebo Bělé pod Pradědem se dá vyrazit na Praděd, k vodopádům Bílé Opavy nebo na Ovčárnu.',
      'V zimě se z chaty jezdí na sjezdovky na Červenohorském sedle, Ramzové nebo do Kouty nad Desnou a na běžky po hřebenech. V létě jsou kolem chat stovky kilometrů turistických a cyklistických tras.',
      'Chaty v Jeseníkách bývají jednodušší, ale s pořádným zázemím. Ukazujeme jen termíny, které jsou právě teď volné.',
    ],
  },
  'jeseniky/pobyty-na-horach': {
    heading: 'Pobyt na horách v Jeseníkách',
    paragraphs: [
      'Hrubý Jeseník je nejvyšší pohoří Moravy. Praděd (1 491 m) je dobře dostupný po asfaltu z Ovčárny, ale hezčí jsou hřebenové túry přes Petrovy kameny, Vysokou holi nebo z Červenohorského sedla na Šerák a Keprník.',
      'Zima tu trvá dlouho a sněhu bývá dost. Lyžuje se na Ramzové, Červenohorském sedle, Dolní Moravě nebo v Koutech nad Desnou, běžkuje kolem Ovčárny a na Rejvízu.',
      'Jeseníky jsou dál od velkých měst, a tak tu je méně lidí než v Krkonoších. Horské pobyty vybírejte z termínů, které jsou právě volné.',
    ],
  },
  'jeseniky/pro-psy': {
    heading: 'Jeseníky se psem',
    paragraphs: [
      'Jeseníky jsou pro psy skvělým místem. Hřebeny, lesy a údolí řek nabízí dost prostoru a turistické trasy nejsou tak přeplněné jako jinde.',
      'S psem se dá jít k vodopádům Bílé Opavy, na Rejvíz k Velkému mechovému jezírku nebo po hřebenech z Ovčárny. V národní přírodní rezervaci Praděd je nutné mít psa na vodítku a držet se značených cest.',
      'V popisu ubytování si zkontrolujte, kolik psů je povoleno. Filtr na mazlíčky je zapnutý automaticky.',
    ],
  },
  'jeseniky/rodinne-pobyty': {
    heading: 'Jeseníky s dětmi',
    paragraphs: [
      'Jeseníky jsou ideální na rodinnou dovolenou. Na Dolní Moravě je Stezka v oblacích s výhledem do okolí, v Jeskyni Na Pomezí krápníky a ve Zlatých Horách zlatorudné mlýny, kde si děti zkusí rýžovat zlato.',
      'Pro menší děti jsou vhodné nenáročné procházky – rašeliniště na Rejvízu po dřevěném chodníku nebo okruh kolem Karlovy Studánky. V zimě se lyžuje na mírných svazích na Ramzové nebo v Koutech.',
      'Rodinné pobyty vybíráme z ubytování s vybavením pro děti. V popisu si ověřte, kolik je ložnic.',
    ],
  },
  'jeseniky/s-krbem': {
    heading: 'Jesenická chalupa s krbem',
    paragraphs: [
      'Zima v Jeseníkách bývá dlouhá a mrazivá, sníh tu leží od listopadu do dubna. Po dni na běžkách kolem Ovčárny nebo na sjezdovce na Červenohorském sedle je krb tím nejlepším, co může chalupa nabídnout.',
      'Krb nebo krbová kamna mají hlavně starší horské chaty a chalupy v Karlově Studánce, Malé Morávce nebo Koutech nad Desnou. V popisu si ověřte, zda je dřevo v ceně.',
      'Ukazujeme jen termíny, které jsou právě teď volné, i na hlavní zimní sezonu.',
    ],
  },
  'jizni-a-stredni-morava/apartmany': {
    heading: 'Apartmán na jižní Moravě',
    paragraphs: [
      'Apartmán je na jižní Moravě nejpraktičtější volba. Bydlíte přímo ve vinařské obci nebo v historickém městě, máte vlastní kuchyň a ke sklepu nebo vinotéce dojdete pěšky – auto může zůstat stát.',
      'Z apartmánu v Mikulově, Valticích, Pavlově nebo Hustopečích se dá vyrazit na Pálavu, do Lednicko-valtického areálu nebo na kole po Moravských vinařských stezkách. Na střední Moravě se hodí Kroměříž nebo Olomouc jako základna pro výlety po okolí.',
      'Nejvíc volných termínů se objevuje mimo vinobraní a velké slavnosti. Ukazujeme jen ty, které jsou právě teď k dispozici.',
    ],
  },
  'jizni-a-stredni-morava/glamping': {
    heading: 'Glamping na Moravě',
    paragraphs: [
      'Morava je pro glamping jako dělaná – teplé léto, dlouhé večery a krajina vinic, sadů a zvlněných kopců. Spát pod širým nebem s výhledem na Pálavu nebo do Bílých Karpat je zážitek i pro ty, kdo stan nesnáší.',
      'Z glampingu můžete vyrazit na kolo po vinařských stezkách, na koupání k Novomlýnským nádržím nebo na výlet do Moravského krasu. Večer pak stačí víno od místního vinaře a hvězdy nad hlavou.',
      'Glampingy mají většinou jen pár jednotek a v létě se rychle plní. Last-minute termíny jsou šance, jak se sem dostat i bez plánování.',
    ],
  },
  'jizni-a-stredni-morava/pobyty-na-horach': {
    heading: 'Moravské hory bez davů',
    paragraphs: [
      'Jižní a střední Morava nemá velehory, ale klidná pohoří plná lesů. Bílé Karpaty s Velkou Javořinou jsou biosférickou rezervací UNESCO a proslavily se orchidejovými loukami, Chřiby skrývají hrad Buchlov a Hostýnské vrchy poutní místo Svatý Hostýn.',
      'Kopce jsou tu nižší a cesty mírnější, takže se dobře chodí i jezdí na kole. V zimě se dá lyžovat na menších svazích v Bílých Karpatech nebo běžkovat po hřebenech, pokud napadne sníh.',
      'Horské pobyty na Moravě vybírejte z termínů, které jsou právě volné. Mezi nabídkami najdete chalupy, chaty i apartmány v kopcích.',
    ],
  },
  'jizni-a-stredni-morava/pro-psy': {
    heading: 'Morava se psem',
    paragraphs: [
      'Morava je pro psy přívětivá: dlouhé vinařské stezky mezi vinicemi, lužní lesy kolem Lednice a soutoku Moravy a Dyje nebo krasová údolí v Moravském krasu nabízejí kilometry cest bez velkého převýšení.',
      'V létě bývá na jižní Moravě horko, a proto se vyplatí chodit se psem ráno nebo večer a nosit dost vody. Do jeskyní v Moravském krasu a do většiny zámků psi nesmějí, takže výlety plánujte s rezervou.',
      'V popisu ubytování zkontrolujte, kolik psů je povoleno. Filtr na mazlíčky je zapnutý automaticky.',
    ],
  },
  'jizni-a-stredni-morava/rodinne-pobyty': {
    heading: 'Jižní a střední Morava s dětmi',
    paragraphs: [
      'Morava nabízí rodinám výlety na každý den. V Moravském krasu se dá projet lodičkou Punkevními jeskyněmi a nahlédnout do propasti Macocha, v Lednici vystoupat na minaret a po Baťově kanálu proplout na lodi.',
      'Pro menší děti jsou ideální rovinaté cyklostezky kolem Novomlýnských nádrží nebo podél Baťova kanálu, v létě koupání v přírodních koupalištích. Kroměříž s Květnou zahradou zase zaujme i ty, kdo zámky nemají rádi.',
      'Rodinné pobyty vybíráme s ohledem na dostatek místa a vybavení pro děti. Termíny se mění každý den.',
    ],
  },
  'jizni-a-stredni-morava/romanticke-pobyty': {
    heading: 'Romantická Morava',
    paragraphs: [
      'Jižní Morava je jedním z nejromantičtějších koutů Česka: zámky Lednice a Valtice, lednický park s rybníky a romantickými stavbami, západ slunce nad Pálavou a večery ve sklípku, kde vám vinař nalije z vlastní produkce.',
      'Pro dva se hodí apartmány ve vinařských obcích, glampingy mezi vinicemi i chalupy v Moravském Toskánsku s výhledem na zvlněná pole. Na střední Moravě nabídne romantiku Kroměříž s Podzámeckou zahradou.',
      'Last-minute pobyt na Moravě je ideální překvapení na výročí nebo jen tak. Rezervaci vyřídíte během pár minut.',
    ],
  },
  'jizni-a-stredni-morava/s-krbem': {
    heading: 'Moravský podzim u krbu',
    paragraphs: [
      'Podzim na Moravě je nejkrásnější: vinobraní, burčák, svatomartinské víno a barevné vinice. Když se ochladí, nic se nevyrovná večeru u krbu s lahví místního vína.',
      'Ubytování s krbem najdete hlavně v chalupách na Slovácku, v Moravském Toskánsku nebo v kopcích Bílých Karpat a Chřibů. V zimě, kdy je na Moravě klid, jsou to ideální místa na odpočinek.',
      'V popisu si ověřte, jestli je dřevo v ceně. Termíny se objevují průběžně.',
    ],
  },
  'jizni-a-stredni-morava/s-virivkou': {
    heading: 'Vířivka mezi moravskými vinicemi',
    paragraphs: [
      'Den na kole po vinařských stezkách mezi Mikulovem a Valticemi umí dát zabrat – a teplá vířivka je pak ideální odměna. V létě venku pod hvězdami, v zimě v teplé koupelně.',
      'Vířivky na Moravě najdete hlavně v moderních apartmánech a chalupách ve vinařských obcích a na Slovácku. Některé jsou na terase s výhledem na vinice.',
      'V popisu si ověřte, jestli je vířivka v ceně a kolik lidí se do ní vejde. Ukazujeme jen volné termíny.',
    ],
  },
  'jizni-a-stredni-morava/se-saunou': {
    heading: 'Sauna na jižní a střední Moravě',
    paragraphs: [
      'Sauna na Moravě není jen zimní zábava. Hodí se po celodenní túře na Svatý Hostýn nebo na Velkou Javořinu, po výletu do Moravského krasu i po dni stráveném na kole mezi vinicemi.',
      'Ubytování se saunou najdete v chalupách v Bílých Karpatech, Chřibech, Hostýnských vrších i v apartmánech ve vinařských obcích. Často je sauna v samostatném domku na zahradě.',
      'V popisu si zkontrolujte, zda je sauna v ceně a jak velká je. Termíny se uvolňují průběžně.',
    ],
  },
  'jizni-a-stredni-morava/wellness-pobyty': {
    heading: 'Wellness na Moravě',
    paragraphs: [
      'Na Moravě se dobře odpočívá. Krajina vinic a kopců, pomalý rytmus vinařských obcí a dobré jídlo samy o sobě uklidní – a sauna nebo vířivka na chalupě je ideálním doplňkem.',
      'Mezi wellness pobyty najdete ubytování se soukromou saunou, vířivkou nebo kádí. Kdo chce lázeňskou proceduru, má na dosah Luhačovice, největší lázně na Moravě.',
      'Wellness na Moravě je oblíbené na podzim a v zimě, kdy je po vinobraní klid. Ukazujeme jen termíny, které jsou právě teď volné.',
    ],
  },
  'krusne-hory/glamping': {
    heading: 'Glamping v Krušných horách',
    paragraphs: [
      'Krušné hory mají otevřenou krajinu s náhorními plošinami, rašeliništi a dlouhými výhledy – ideální pro glamping. Spíte v přírodě, ale v pohodlí, a ráno vyrazíte rovnou na hřeben.',
      'Z glampingu se dá vyrazit k Božídarskému rašeliništi, na Klínovec nebo podél Blatenského vodního příkopu, technické památky z 16. století zapsané v UNESCO. Na hřebenech fouká a noci jsou chladné, proto si ověřte, jak se v glampingu topí.',
    ],
  },
  'krusne-hory/pobyty-na-horach': {
    heading: 'Pobyt v Krušných horách',
    paragraphs: [
      'Krušné hory jsou dlouhý hřbet táhnoucí se podél hranice se Saskem. Nejvyšší Klínovec (1 244 m) je centrem lyžování, kousek od něj leží Boží Dar a na východě hory pokračují k Mostu a Teplicím, kde jsou známá střediska Bouřňák nebo Telnice.',
      'V zimě vede po hřebenech Krušnohorská lyžařská magistrála, v létě cyklotrasy a turistické cesty k rozhlednám, rašeliništím a hornickým památkám. Hornický region Krušnohoří je od roku 2019 zapsaný na seznamu UNESCO.',
      'Krušné hory jsou klidnější a cenově dostupnější než Krkonoše. Vybírejte z horských pobytů, které jsou právě volné.',
    ],
  },
  'krusne-hory/pro-psy': {
    heading: 'Krušné hory se psem',
    paragraphs: [
      'Krušné hory jsou pro psy ideální – rozlehlé náhorní plošiny, lesní cesty bez davů a mírné stoupání. Na dlouhou procházku se dá vyrazit z Božího Daru, Horní Blatné nebo Moldavy.',
      'Na rašeliništích je potřeba držet psa na vodítku a zůstat na chodnících, aby nerušil zvěř a neničil vzácnou vegetaci. V zimě pozor na posypové soli na silnicích – po procházce psovi omyjte tlapky.',
      'V popisu ubytování si ověřte pravidla pro psy. Filtr na mazlíčky je zapnutý automaticky.',
    ],
  },
  'krusne-hory/romanticke-pobyty': {
    heading: 'Romantický pobyt v Krušných horách',
    paragraphs: [
      'Krušné hory jsou pro dva nečekaně romantické. Boží Dar je nejvýše položené město ve střední Evropě, hřebeny bývají zahalené mlhou a krajina působí trochu seversky.',
      'Pro pobyt ve dvou se hodí menší chalupy a apartmány v horských obcích nebo glampingy na samotě. Na výlet se dá zajet do lázní v Jáchymově nebo přes hranici do saského Oberwiesenthalu.',
      'Ukazujeme jen termíny, které jsou právě volné – i na poslední víkend.',
    ],
  },
  'krusne-hory/s-krbem': {
    heading: 'Krušnohorská chalupa s krbem',
    paragraphs: [
      'Zima v Krušných horách je dlouhá a větrná a sníh tu leží často až do dubna. Po dni na běžkách po magistrále nebo na sjezdovce na Klínovci je krb tím nejlepším místem v domě.',
      'Chalupy a chaty s krbem najdete v Božím Daru, Loučné, Perninku i ve východní části hor kolem Moldavy. Mnohé jsou staré horské domy po původních obyvatelích.',
      'V popisu si ověřte, jestli je dřevo v ceně. Termíny se tu objevují průběžně.',
    ],
  },
  'krusne-hory/se-saunou': {
    heading: 'Sauna v Krušných horách',
    paragraphs: [
      'Krušnohorská zima je mrazivá a větrná – sauna je tu proto víc než luxus. Po sjezdovkách na Klínovci nebo běžkách kolem Božího Daru se v ní prohřejete a ochladíte ve sněhu.',
      'Ubytování se saunou najdete v horských chalupách, apartmánech i v menších penzionech. Pro opravdové lázně je kousek do Jáchymova s radonovými procedurami.',
      'V popisu si ověřte, jestli je sauna v ceně a kolik lidí se do ní vejde.',
    ],
  },
  'vychodni-cechy/glamping': {
    heading: 'Glamping ve východních Čechách',
    paragraphs: [
      'Východní Čechy nabízejí pro glamping skvělé kulisy: skalní města Adršpach a Teplice nad Metují, Broumovské stěny nebo podhůří Orlických hor. Spíte v přírodě, ale s pohodlím postele.',
      'Z glampingu se dá vyrazit na túru skalami, na kolo po Broumovsku nebo za památkami – barokním Kuksem, Babiččiným údolím nebo zámkem v Ratibořicích. Noci v podhůří bývají chladné, proto si ověřte vytápění.',
      'Glampingy mají jen málo jednotek a v létě se rychle obsazují. Sledujte nabídku a vyrazte na poslední chvíli.',
    ],
  },
  'vychodni-cechy/pro-psy': {
    heading: 'Východní Čechy se psem',
    paragraphs: [
      'Východní Čechy jsou pro psy plné příležitostí. Babiččino údolí s cestou podél Úpy, pískovcové Toulovcovy maštale, hřebenovka po Broumovských stěnách nebo lesy Orlických hor.',
      'Do Adršpašsko-teplických skal se se psem smí, na úzkých stezkách a žebřících ale musí být na vodítku a pod kontrolou – stejně jako v ostatních přírodních rezervacích.',
      'V popisu ubytování zkontrolujte, kolik psů je povoleno a zda se za ně platí. Filtr na mazlíčky je zapnutý automaticky.',
    ],
  },
  'vychodni-cechy/rodinne-pobyty': {
    heading: 'Východní Čechy s dětmi',
    paragraphs: [
      'Východní Čechy jsou pro rodiny jako stvořené. Safari Park Dvůr Králové patří k nejznámějším zoologickým zahradám v Evropě, Adršpašské skály jsou pro děti obřím bludištěm a Babiččino údolí si pamatují ze školy.',
      'Na výlety jsou tu i Kuks, Kunětická hora s hradem, perníková Pardubice nebo pevnost Josefov s podzemními chodbami. V zimě se dá lyžovat v Orlických horách nebo v Podkrkonoší.',
      'Rodinné pobyty vybíráme s ohledem na místo pro děti. Ukazujeme jen volné termíny.',
    ],
  },
  'vychodni-cechy/romanticke-pobyty': {
    heading: 'Romantické východní Čechy',
    paragraphs: [
      'Východní Čechy nabízejí pro dva hodně romantiky: barokní Kuks s Braunovými sochami, zámek Litomyšl s arkádami, Babiččino údolí s mlýnem nebo skalní města, kde ráno nikdo není.',
      'Pro pobyt ve dvou se hodí menší chalupy na Broumovsku, roubenky v Podkrkonoší nebo glampingy s výhledem. Kraj je méně turistický než Krkonoše, a proto i klidnější.',
    ],
  },
  'vychodni-cechy/s-krbem': {
    heading: 'Chalupa s krbem ve východních Čechách',
    paragraphs: [
      'Podzim na Broumovsku a v Podkrkonoší je čas mlh, barevných lesů a dlouhých večerů. Chalupa s krbem je pak ideálním místem, kde se dá číst, hrát hry a nikam nespěchat.',
      'Krb mají hlavně roubenky a starší chalupy v podhůří Orlických hor a Krkonoš. Přes den se dá vyrazit do skal nebo na zámek, večer se vrátit k ohni.',
      'V popisu si ověřte, jestli je dřevo v ceně. Termíny se mění každý den.',
    ],
  },
  'vychodni-cechy/se-saunou': {
    heading: 'Sauna ve východních Čechách',
    paragraphs: [
      'Sauna je ideální po túře skalním městem v Adršpachu, výstupu na Ostaš nebo dni na kole po Broumovsku. Po celodenním pohybu prohřeje svaly a večer je pak opravdu odpočinkový.',
      'Ubytování se saunou najdete v chalupách, roubenkách i moderních domech od Broumovska po podhůří Orlických hor. Často je sauna sudová na zahradě nebo v samostatném domku.',
      'V popisu si zkontrolujte, zda je sauna v ceně. Ukazujeme jen termíny, které jsou právě teď volné.',
    ],
  },
  'vychodni-cechy/wellness-pobyty': {
    heading: 'Wellness ve východních Čechách',
    paragraphs: [
      'Východní Čechy mají lázeňskou tradici – Lázně Velichovky, Lázně Bělohrad nebo Janské Lázně v sousedních Krkonoších. Pro soukromý odpočinek ale stačí chalupa se saunou nebo vířivkou.',
      'Wellness pobyty vybíráme z ubytování, kde máte saunu, vířivku nebo káď jen pro sebe. Přes den se dá vyrazit na výlet do skal nebo na zámek, večer relaxovat.',
      'Sledujte last-minute nabídky – volné termíny se objevují průběžně.',
    ],
  },
  'vysocina/glamping': {
    heading: 'Glamping na Vysočině',
    paragraphs: [
      'Vysočina je krajem lesů, luk a rybníků – ideální pro glamping. Spíte v přírodě, ale v pohodlí, a večer slyšíte jen ptáky a vítr v korunách.',
      'Z glampingu se dá vyrazit na túru do Žďárských vrchů k Devíti skalám nebo na Pasecké skály, k rybníkům Velké Dářko a Medlov nebo na kolo po klidných silničkách. Noci na Vysočině bývají chladné, proto si ověřte vytápění.',
      'Glampingy mají málo jednotek a rychle se obsazují. Ukazujeme jen termíny, které jsou právě volné.',
    ],
  },
  'vysocina/pro-psy': {
    heading: 'Vysočina se psem',
    paragraphs: [
      'Vysočina je pro psy ideální: lesy, louky a hráze rybníků, kde se dá chodit celé hodiny. Turistů je tu méně než v horách, takže se pes vyběhá v klidu.',
      'S psem se dá vyrazit k Devíti skalám, kolem Velkého Dářka nebo podél Sázavy. V lese a u pastvin ale mějte psa na vodítku – kvůli zvěři i stádům.',
      'V popisu ubytování si ověřte pravidla pro psy. Filtr na mazlíčky je zapnutý automaticky.',
    ],
  },
  'vysocina/rodinne-pobyty': {
    heading: 'Vysočina s dětmi',
    paragraphs: [
      'Vysočina je skvělá pro rodiny s dětmi. Telč s pohádkovým náměstím, Pelhřimov s muzeem rekordů a kuriozit, hrad Lipnice s výhledem do kraje nebo koupání ve Velkém Dářku.',
      'Krajina je klidná a cesty nenáročné, takže zvládnete i výlety s malými dětmi. V zimě se dá jezdit na běžkách kolem Nového Města na Moravě nebo bobovat na mírných kopcích.',
      'Rodinné pobyty vybíráme z chalup a statků s dostatkem místa. Termíny se mění každý den.',
    ],
  },
  'vysocina/romanticke-pobyty': {
    heading: 'Romantický víkend na Vysočině',
    paragraphs: [
      'Vysočina je ideální pro víkend ve dvou. Leží uprostřed republiky, takže sem dojedete rychle z Prahy i z Brna, a přesto tu najdete ticho, lesy a krajinu, kde se zastavil čas.',
      'Na výlet se hodí renesanční Telč, poutní kostel na Zelené hoře nebo procházka po Žďárských vrších. Večer pak stačí chalupa jen pro vás dva.',
      'Romantické pobyty vybíráme s ohledem na soukromí. Ukazujeme jen termíny, které jsou právě volné.',
    ],
  },
  'vysocina/s-krbem': {
    heading: 'Vysočina u krbu',
    paragraphs: [
      'Vysočina patří k chladnějším krajům Česka a zima tu umí být pořádná. Po procházce zasněženými lesy nebo dni na běžkách kolem Nového Města na Moravě je krb ideálním místem, kde se ohřát.',
      'Krb nebo kamna mají hlavně zrekonstruované statky, roubenky a chalupy v malých vesnicích. Mnohé z nich jsou starší domy s tradičními kachlovými kamny.',
      'V popisu si ověřte, jestli je dřevo v ceně. Volné termíny se objevují průběžně.',
    ],
  },
  'vysocina/s-virivkou': {
    heading: 'Vířivka na Vysočině',
    paragraphs: [
      'Po dni na kole po Žďárských vrších nebo procházce kolem rybníků se v teplé vířivce dokonale uvolníte. A večer pod hvězdami bez světelného hluku měst je na Vysočině ještě lepší.',
      'Ubytování s vířivkou najdete v modernějších chalupách a statcích po celé Vysočině. Některé vířivky jsou venku na terase, jiné v interiéru.',
      'V popisu si ověřte, jestli je vířivka k dispozici po celý rok a kolik lidí se do ní vejde.',
    ],
  },
  'vysocina/wellness-pobyty': {
    heading: 'Wellness na Vysočině',
    paragraphs: [
      'Vysočina je sama o sobě klidná a odpočinková – málo lidí, hodně lesů a pomalý rytmus. K tomu sauna nebo vířivka na chalupě a máte ideální wellness víkend.',
      'Mezi wellness pobyty najdete chalupy a statky se soukromou saunou, vířivkou nebo kádí. Přes den se dá vyrazit na procházku nebo do Telče či Třebíče, večer už jen odpočívat.',
      'Vysočina je kousek od Prahy i Brna, takže se dá vyrazit i na poslední chvíli.',
    ],
  },
  'zapadni-cechy/glamping': {
    heading: 'Glamping v západních Čechách',
    paragraphs: [
      'Západní Čechy nabízejí pro glamping lesy Slavkovského a Českého lesa, údolí Ohře i krajinu kolem lázeňských měst. Spíte v přírodě, ale v pohodlí postele.',
      'Z glampingu se dá vyrazit na naučnou stezku Kladská po dřevěném chodníku rašeliništěm, do přírodní rezervace Soos s bahenními sopkami nebo na hrad Loket. Noci bývají chladné, ověřte si proto vytápění.',
      'Glampingy mají málo jednotek a rychle se plní. Sledujte last-minute nabídky.',
    ],
  },
  'zapadni-cechy/romanticke-pobyty': {
    heading: 'Romantický pobyt v západních Čechách',
    paragraphs: [
      'Západní Čechy mají pro dva romantiku lázeňských měst: procházku kolonádou v Mariánských Lázních, Zpívající fontánu, karlovarské vřídlo nebo hrad Loket nad Ohří, sevřený v meandru řeky.',
      'Pro pobyt ve dvou se hodí menší chalupy ve Slavkovském lese nebo apartmány kousek od lázní. Večer můžete zajít na koncert na kolonádě, nebo zůstat jen spolu.',
    ],
  },
  'zapadni-cechy/se-saunou': {
    heading: 'Sauna v západních Čechách',
    paragraphs: [
      'Západní Čechy jsou krajem lázní a odpočinku má tu tradici. Vlastní sauna na chalupě je soukromou variantou – bez rezervací a bez davů, jen pro vás.',
      'Ubytování se saunou najdete v chalupách ve Slavkovském a Českém lese i kolem Chebu a Lokte. Sauna je skvělá po procházce lesem nebo po výletu do lázní.',
      'V popisu si ověřte, jestli je sauna v ceně. Ukazujeme jen termíny, které jsou právě volné.',
    ],
  },
  'zapadni-cechy/wellness-pobyty': {
    heading: 'Wellness v kraji lázní',
    paragraphs: [
      'Karlovy Vary, Mariánské Lázně a Františkovy Lázně patří mezi Slavné lázně Evropy zapsané v UNESCO. Na lázeňské procedury se dá zajet na den, a pak se vrátit do soukromí vlastní chalupy.',
      'Mezi wellness pobyty najdete ubytování se saunou, vířivkou nebo kádí, které máte jen pro sebe. Přes den procházka kolonádou nebo lesem, večer relax.',
      'Wellness pobyty v západních Čechách jsou oblíbené celý rok. Sledujte nabídku – volné termíny se objevují průběžně.',
    ],
  },
}
